if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupPickupCalendar();
        this.setupPickupGiftOptions();
        this.setupPickupGiftDisplay();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (!this.validatePickupOptions()) return;

        const formData = new FormData(this.form);
        if (!(await this.validateCartMix(formData))) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      setupPickupCalendar() {
        this.pickupCalendar = this.querySelector('[data-pickup-calendar]');
        if (!this.pickupCalendar) return;

        this.pickupDateInput = this.pickupCalendar.querySelector('[data-pickup-date-input]');
        this.pickupTimeSelect = this.pickupCalendar.querySelector('[data-pickup-time-select]');
        this.pickupSelectedDate = this.pickupCalendar.querySelector('[data-pickup-selected-date]');
        this.pickupCalendarGrid = this.pickupCalendar.querySelector('[data-pickup-calendar-grid]');
        this.pickupStartDate = this.parseLocalDate(this.pickupCalendar.dataset.startDate);
        this.pickupEndDate = this.pickupCalendar.dataset.endDate
          ? this.parseLocalDate(this.pickupCalendar.dataset.endDate)
          : null;
        this.pickupDisabledDates = new Set(
          (this.pickupCalendar.dataset.disabledDates || '')
            .split(',')
            .map((date) => date.trim())
            .filter(Boolean)
        );
        this.pickupCurrentMonth = new Date(this.pickupStartDate.getFullYear(), this.pickupStartDate.getMonth(), 1);

        this.renderPickupCalendar();
      }

      validatePickupOptions() {
        if (!this.pickupCalendar || !this.pickupDateInput) return true;

        if (!this.pickupDateInput.value) {
          this.pickupCalendar.classList.add('pickup-date-selector--error');
          this.handleErrorMessage('受取日を選択してください。');
          this.pickupCalendar.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        if (this.pickupTimeSelect && !this.pickupTimeSelect.value) {
          this.pickupCalendar.classList.add('pickup-date-selector--error');
          this.handleErrorMessage('受取時刻を選択してください。');
          this.pickupTimeSelect.focus();
          return false;
        }

        if (this.pickupDateInput.value) {
          this.pickupCalendar.classList.remove('pickup-date-selector--error');
          return true;
        }

        return true;
      }

      async validateCartMix(formData) {
        const currentFulfillmentType = formData.get('properties[_fulfillment_type]') === 'pickup' ? 'pickup' : 'delivery';

        try {
          const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return true;

          const cart = await response.json();
          const hasPickupItem = cart.items.some((item) => item.properties && item.properties._fulfillment_type === 'pickup');
          const hasDeliveryItem = cart.items.some((item) => !item.properties || item.properties._fulfillment_type !== 'pickup');

          if ((currentFulfillmentType === 'pickup' && hasDeliveryItem) || (currentFulfillmentType === 'delivery' && hasPickupItem)) {
            this.handleErrorMessage('店舗受取商品と配送商品は同じカートで購入できません。別々にご注文ください。');
            return false;
          }
        } catch (error) {
          console.error(error);
        }

        return true;
      }

      setupPickupGiftDisplay() {
        if (!this.querySelector('[data-pickup-calendar]')) return;
        if (!document.querySelector('.sns-gift-product-container')) return;

        let giftUpdateFrame = null;

        const hideStorePickupGiftFields = () => {
          const giftContainer =
            this.querySelector('.sns-gift-product-container') || document.querySelector('.sns-gift-product-container');
          if (!giftContainer) return;

          this.restoreProductSubmitButton();

          giftContainer.querySelectorAll('[data-testid^="open-shipping-address-settings"]').forEach((element) => {
            element.hidden = true;
            const nextDivider = element.nextElementSibling;
            if (nextDivider && nextDivider.tagName === 'HR') nextDivider.hidden = true;
          });

          giftContainer.querySelectorAll('p, button').forEach((element) => {
            const text = element.textContent.trim();
            if (
              text === '贈り物をする送付先ごとにギフト設定を行ってください。' ||
              /^贈る相手\s*\[/.test(text) ||
              text === 'この設定を複製する' ||
              text === '贈る相手を追加'
            ) {
              element.hidden = true;
            }
          });

          giftContainer.querySelectorAll('[data-testid^="open-gift-settings"]').forEach((element) => {
            element.hidden = false;
            const giftRow = element.parentElement;
            if (giftRow) giftRow.hidden = false;
          });

          const checkoutButton = giftContainer.querySelector('#all-in-gift-checkout-button');
          if (checkoutButton) {
            if (!checkoutButton.hidden) checkoutButton.hidden = true;
            if (checkoutButton.style.getPropertyValue('display') !== 'none') checkoutButton.style.setProperty('display', 'none', 'important');
            const checkoutButtonWrapper = checkoutButton.closest('div');
            if (checkoutButtonWrapper) {
              if (!checkoutButtonWrapper.hidden) checkoutButtonWrapper.hidden = true;
              if (checkoutButtonWrapper.style.getPropertyValue('display') !== 'none')
                checkoutButtonWrapper.style.setProperty('display', 'none', 'important');
            }
          }
        };

        const scheduleGiftDisplayUpdate = () => {
          if (giftUpdateFrame) return;
          giftUpdateFrame = window.requestAnimationFrame(() => {
            giftUpdateFrame = null;
            hideStorePickupGiftFields();
          });
        };

        hideStorePickupGiftFields();

        document.addEventListener('click', (event) => {
          if (event.target.closest('.sns-gift-product-container')) {
            window.setTimeout(scheduleGiftDisplayUpdate, 0);
            window.setTimeout(scheduleGiftDisplayUpdate, 300);
          }
        });

        document.addEventListener('change', (event) => {
          if (event.target.closest('.sns-gift-product-container')) {
            window.setTimeout(scheduleGiftDisplayUpdate, 0);
            window.setTimeout(scheduleGiftDisplayUpdate, 300);
          }
        });

        new MutationObserver(scheduleGiftDisplayUpdate).observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      setupPickupGiftOptions() {
        const giftOptions = this.querySelector('[data-pickup-gift]');
        if (!giftOptions) return;

        const toggle = giftOptions.querySelector('[data-pickup-gift-toggle]');
        const fieldsWrapper = giftOptions.querySelector('[data-pickup-gift-fields]');
        const fields = giftOptions.querySelectorAll('[data-pickup-gift-field]');
        if (!toggle || !fieldsWrapper) return;

        const updateGiftFields = () => {
          const enabled = toggle.checked;
          fieldsWrapper.hidden = !enabled;
          fields.forEach((field) => {
            field.disabled = !enabled;
          });
        };

        toggle.addEventListener('change', updateGiftFields);
        updateGiftFields();
      }

      restoreProductSubmitButton() {
        if (!this.submitButton) return;

        if (this.submitButton.hidden) this.submitButton.hidden = false;
        if (this.submitButton.style.getPropertyValue('visibility') !== 'visible')
          this.submitButton.style.setProperty('visibility', 'visible', 'important');
        if (this.submitButton.style.getPropertyValue('display')) this.submitButton.style.removeProperty('display');

        const buttonsWrapper = this.submitButton.closest('.product-form__buttons');
        if (buttonsWrapper) {
          if (buttonsWrapper.hidden) buttonsWrapper.hidden = false;
          if (buttonsWrapper.style.getPropertyValue('display')) buttonsWrapper.style.removeProperty('display');
          if (buttonsWrapper.style.getPropertyValue('visibility')) buttonsWrapper.style.removeProperty('visibility');
        }
      }

      renderPickupCalendar() {
        if (!this.pickupCalendarGrid) return;

        const month = this.pickupCurrentMonth;
        const year = month.getFullYear();
        const monthIndex = month.getMonth();
        const firstDay = new Date(year, monthIndex, 1);
        const startOffset = firstDay.getDay();
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
        const selectedDate = this.pickupDateInput.value;
        const monthLabel = `${year}年${monthIndex + 1}月`;
        const minMonth = new Date(this.pickupStartDate.getFullYear(), this.pickupStartDate.getMonth(), 1);
        const maxMonth = this.pickupEndDate
          ? new Date(this.pickupEndDate.getFullYear(), this.pickupEndDate.getMonth(), 1)
          : null;
        const canGoPrevious = month > minMonth;
        const canGoNext = !maxMonth || month < maxMonth;
        const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

        this.pickupCalendarGrid.innerHTML = `
          <div class="pickup-calendar__header">
            <button class="pickup-calendar__nav" type="button" data-pickup-prev ${canGoPrevious ? '' : 'disabled'} aria-label="前の月へ">‹</button>
            <div class="pickup-calendar__month" aria-live="polite">${monthLabel}</div>
            <button class="pickup-calendar__nav" type="button" data-pickup-next ${canGoNext ? '' : 'disabled'} aria-label="次の月へ">›</button>
          </div>
          <div class="pickup-calendar__weekdays">
            ${weekdays.map((day) => `<span>${day}</span>`).join('')}
          </div>
          <div class="pickup-calendar__days"></div>
        `;

        const daysContainer = this.pickupCalendarGrid.querySelector('.pickup-calendar__days');

        for (let i = 0; i < startOffset; i += 1) {
          daysContainer.insertAdjacentHTML('beforeend', '<span class="pickup-calendar__empty"></span>');
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
          const date = new Date(year, monthIndex, day);
          const value = this.formatLocalDate(date);
          const isDisabled =
            date < this.pickupStartDate ||
            (this.pickupEndDate && date > this.pickupEndDate) ||
            this.pickupDisabledDates.has(value);
          const isSelected = value === selectedDate;
          const className = `pickup-calendar__day${isSelected ? ' is-selected' : ''}`;

          daysContainer.insertAdjacentHTML(
            'beforeend',
            `<button class="${className}" type="button" data-pickup-date="${value}" ${isDisabled ? 'disabled' : ''} aria-pressed="${isSelected}">${day}</button>`
          );
        }

        this.pickupCalendarGrid.querySelector('[data-pickup-prev]')?.addEventListener('click', () => {
          this.pickupCurrentMonth = new Date(year, monthIndex - 1, 1);
          this.renderPickupCalendar();
        });

        this.pickupCalendarGrid.querySelector('[data-pickup-next]')?.addEventListener('click', () => {
          this.pickupCurrentMonth = new Date(year, monthIndex + 1, 1);
          this.renderPickupCalendar();
        });

        this.pickupCalendarGrid.querySelectorAll('[data-pickup-date]').forEach((button) => {
          button.addEventListener('click', () => {
            this.pickupDateInput.value = button.dataset.pickupDate;
            this.pickupCalendar.classList.remove('pickup-date-selector--error');
            if (this.pickupSelectedDate) this.pickupSelectedDate.textContent = `選択中の受取日：${this.formatDisplayDate(button.dataset.pickupDate)}`;
            this.handleErrorMessage();
            this.renderPickupCalendar();
          });
        });
      }

      parseLocalDate(value) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      formatLocalDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      formatDisplayDate(value) {
        const date = this.parseLocalDate(value);
        const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
        return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekdays[date.getDay()]}）`;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
